import { MissionAvailabilityReason } from '@/features/missions/api/models'
import { type MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'

export type NectarProjectType =
  | 'AUDIO_RECORDING'
  | 'VOICE_CONVERSATION'
  | 'PROFICIENCY_ASSESSMENT'
  | 'FREE_SPEECH'
  | 'AUDIO_ANNOTATION'
  | 'AUDIO_TRANSCRIPTION'

export const NECTAR_TYPE_TEXT_MAP: Record<string, MessageDescriptor> = {
  AUDIO_RECORDING: msg`Audio Recording`,
  VOICE_CONVERSATION: msg`Voice Conversation`,
  PROFICIENCY_ASSESSMENT: msg`Proficiency Assessment`,
  FREE_SPEECH: msg`Free Speech`,
  AUDIO_ANNOTATION: msg`Audio Annotation`,
  AUDIO_TRANSCRIPTION: msg`Audio Transcription`
}

export type AudioAnnotationFieldOption = { value: string; label: string }

type AudioAnnotationFieldBase = {
  key: string
  label: string
  description?: string
  required: boolean
}

export type AudioAnnotationSingleSelectField = AudioAnnotationFieldBase & {
  type: 'single-select'
  options: AudioAnnotationFieldOption[]
}

export type AudioAnnotationMultiSelectField = AudioAnnotationFieldBase & {
  type: 'multi-select'
  options: AudioAnnotationFieldOption[]
  minSelected?: number
  maxSelected?: number
}

export type AudioAnnotationToggleField = AudioAnnotationFieldBase & {
  type: 'toggle'
}

export type AudioAnnotationRatingField = AudioAnnotationFieldBase & {
  type: 'rating'
  min: number
  max: number
}

export type AudioAnnotationField =
  | AudioAnnotationSingleSelectField
  | AudioAnnotationMultiSelectField
  | AudioAnnotationToggleField
  | AudioAnnotationRatingField

export type AudioAnnotationFormSchema = {
  fields: AudioAnnotationField[]
}

export type AnnotatorPretestTaskSubmission = {
  taskId: string
  fields: { fieldKey: string; answer: unknown }[]
}

export type AnnotatorPretestResult = {
  status: 'PASSED' | 'FAILED'
}

export type AudioAnnotationTaskDetail = {
  id: string
  instruction: string
  type: 'audio-annotation'
  phrase?: string
  topic?: string
  formSchema: AudioAnnotationFormSchema
  audioUrl?: string
  audioUrlExpiresAt?: string
  metadata: Record<string, unknown> | null
  projectId: string
  createdAt: string
  updatedAt: string
}

export type AudioTranscriptionSegment = {
  end: number
  start: number
  text: string | null
  id?: string
  source?: string
  tags?: Record<string, unknown>
}

export type AudioTranscriptionTypeConfig = {
  editing?: { allowSegmentEdit?: boolean }
  limits?: { maxSegments?: number; maxTextChars?: number }
  segmentPolicy?: { minSec?: number; hardMaxSec?: number; targetMaxSec?: number; targetMinSec?: number }
}

export type AudioTranscriptionTaskDetail = {
  id: string
  instruction: string
  type: 'audio-transcription'
  formSchema: AudioAnnotationFormSchema
  audioUrl: string
  audioUrlExpiresAt: string
  clipDurationSec: number
  segments: AudioTranscriptionSegment[]
  metadata: Record<string, unknown> | null
  projectId: string
  createdAt: string
  updatedAt: string
  textOptional: {
    fieldKey: string
  }
}

export type FreeSpeechTaskParams = { topic: string; hint?: string }

export type FreeSpeechTypeConfig = {
  minDurationSec: number
  maxDurationSec: number
  maxSkipsPerUser: number
}

export type TaskStats = {
  completed: number
  target: number
  skipsUsed: number
  skipsRemaining: number
}

export type FreeSpeechTaskPayload = {
  id: string
  instruction: string
  params: Record<string, unknown>
  metadata?: Record<string, unknown> | null
}

export type NextTaskResponse = {
  task: FreeSpeechTaskPayload
  stats: TaskStats
}

export type SkipResponse = { stats: TaskStats }

export type NectarApiErrorBody = { error: { code: string; message: string } }

export type CefrLevel = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2'

export type ProficiencyAssessmentFinalResult = {
  level: CefrLevel
  numericScore: number
  confidence: 'low' | 'medium' | 'high'
  signals: { selfAssessment: CefrLevel; textTest: CefrLevel; speechTest: CefrLevel }
}

export type AssessmentPhaseStatus = 'pending' | 'active' | 'done'
export type AssessmentCurrentPhase = 'text' | 'speech' | 'completing' | 'completed'

export type TextPhaseProgress = {
  kind: 'text'
  status: AssessmentPhaseStatus
  answeredCount: number
  minQuestions: number
  maxQuestions: number
  perQuestionTimeLimitSec: number
}

export type SpeechPhaseProgress = {
  kind: 'speech'
  status: AssessmentPhaseStatus
  answeredCount: number
  totalQuestions: number
}

export type AssessmentProgress = {
  currentPhase: AssessmentCurrentPhase
  phases: [TextPhaseProgress, SpeechPhaseProgress]
  totalDurationMin: number
}

export type ProficiencyAssessment = {
  id: string
  status: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED'
  finalResult: ProficiencyAssessmentFinalResult | null
  expiresAt: string | null
  createdAt: string
  progress: AssessmentProgress
}

export type TextQuestion = {
  id: string
  prompt: string
  options: { text: string }[]
  timeLimitSec: number | null
}

export type SpeechQuestion = {
  id: string
  audioUrl: string
}

export type NectarProjectStatus = 'DRAFT' | 'ACTIVE' | 'COMPLETED' | 'ARCHIVED'
export type NectarEnrollmentStatus = 'IN_PROGRESS' | 'PENDING_REVIEW' | 'APPROVED' | 'REJECTED' | 'EXPIRED'

export type NectarRewardConfig = {
  type: string
  amount: number
} | null

export type NectarProject = {
  id: string
  name: string
  type: NectarProjectType
  description: string | null
  rules: string | null
  language: string
  screeningLanguage: string | null
  status: NectarProjectStatus
  maxTasksPerUser: number
  maxSubmissionsPerTask: number
  labelFormSchema: AudioAnnotationFormSchema | null
  approvalThreshold: number
  assignmentTtlHours: number | null
  rewardConfig: NectarRewardConfig
  totalReward: number | null
  createdAt: string
  updatedAt: string
  captchaEnabled: boolean
  accessGateEnabled: boolean
  accessGateKind: 'LANGUAGE' | 'CONSENT'
  accessGatePhrase: string | null
  typeConfig?: Record<string, unknown> | null
  enrolled: boolean
  progress: { completed: number; total: number }
  enrollment: { id: string; status: NectarEnrollmentStatus; earnedReward: number | null } | null
  enrolledUsersCount: number
  sortOrder: number
  availability: {
    available: boolean
    reasons: {
      type: MissionAvailabilityReason
      completed?: boolean
      passed?: boolean
      required?: boolean
      language?: string
    }[]
  }
  metadata: {
    estimatedMinutes: number | null
  }
  whitelistEnabled: boolean
  // Per-user whitelist membership. Present on authorized reads (`/v1/projects/:id`); absent on
  // public/unauthorized reads, where it's treated as not whitelisted.
  isUserWhitelisted?: boolean
  taskReportingEnabled: boolean
}

export type NectarTask = {
  id: string
  instruction: string
  type: string
  params: Record<string, unknown>
  metadata: Record<string, unknown> | null
  projectId: string
  createdAt: string
  updatedAt: string
  submissionCount: number
}

export type PaginatedResponse<T> = {
  data: T[]
  meta: { total: number; page: number; limit: number }
}

export type EnrollResponse = {
  projectId: string
  enrollmentId: string
  assignedCount: number
}
